import React, { useEffect, useState, useMemo, useCallback } from "react";
import { data, useLocation, useNavigate } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import Button from "../../components/button";
import ChatHeader from "../../components/chat-header";
import { ExerciseQuestionsService } from "../../services/exercise-questions/exercise-questions.service";
import { useLayoutContext } from "../../layouts/general-layout"; // Import context
import "./exercise-questions.scss";
import MathExpressionNew from '../../components/common/mathjax';
import { showErrorToast } from "../../components/Toaster/index";
import { vIcon } from "../../assets/images/index";
import { useQuestionsStore } from "../../utils/questionStore";
import { useExerciseStore } from "../../utils/exerciseStore";
import ExerciseQuestionList from "../../components/exercise-question-list";
import { AudioService } from "../../services/text-to-audio/texttoaudio.service";
import { hebrewLetters } from '../../configs/config';
import { TrackingService } from "../../services/tracking/tracking.service";


export default function Index() {
  const location = useLocation();
  const selectedExercise = location.state?.selectedExercise;
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;
  const { t } = useLayoutContext(); // Use the context instead of useTranslation
  const [exerciseQuestions, setExerciseQuestions] = useState<
    { id: number; question: string; chart_image_data?: string }[]
  >([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [selectedQuestionLetter, setSelectedQuestionLetter] = useState<string | null>(null);
  const selectedExerciseId = useExerciseStore((s) => s.selectedExerciseId);
  const completedQuestions = useMemo(() => {
    const history = useQuestionsStore.getState().getQuestionHistory(selectedExerciseId);
    return history?.completed || [];
  }, [selectedExerciseId]);
  const audioServiceInstance = new AudioService();
  const trackingServiceInstance = new TrackingService();
  const setCurrentSelectedQuestion = useQuestionsStore(state => state.setCurrentSelectedQuestion);

  const exerciseFilename = useExerciseStore((s) => s.selectedExerciseName);

  useEffect(() => {
    if (!exerciseFilename && selectedExercise?.filename) {
      // update only if filename is actually different
      useExerciseStore.setState((prev: any) => {
        if (prev.exerciseFilename !== selectedExercise.filename) {
          return { exerciseFilename: selectedExercise.filename };
        }
        return prev;
      });
    }
  }, [exerciseFilename, selectedExercise?.filename]);

  useEffect(() => {
    handleDisplayExerciseQuestions();
  }, []);

  useEffect(() => {
    if (exerciseQuestions.length > 0) {
      const nextQuestionIndex = exerciseQuestions.findIndex(
        (q) => !completedQuestions.includes(q.id)
      );

      if (nextQuestionIndex !== -1) {
        const nextQuestion = exerciseQuestions[nextQuestionIndex];
        const hebrewLetter = hebrewLetters[nextQuestionIndex];
        handleSelectQuestion(nextQuestion, hebrewLetter);
      } else if (completedQuestions.length === 0) {
        const firstQuestion = exerciseQuestions[0];
        const hebrewLetter = hebrewLetters[0];
        handleSelectQuestion(firstQuestion, hebrewLetter);
      } else {
        setSelectedQuestionId(null);
        setSelectedQuestion(null);
        setSelectedQuestionLetter(null);
        useQuestionsStore.getState().setCurrentSelectedQuestion(null);
      }
    }
  }, [exerciseQuestions, completedQuestions]);


  const handleTracking = (event_name: string) => {
    trackingServiceInstance.trackEvent(event_name, {
      source: "questionspage",
      selected_exercise: exerciseFilename || selectedExercise?.filename || "",
      selected_question: selectedQuestionLetter
    });
  }

  const handleDisplayExerciseQuestions = async () => {
    const exerciseServiceInstance = new ExerciseQuestionsService();
    const questions = await exerciseServiceInstance.fetchExerciseQuestions(selectedExerciseId);

    const history = useQuestionsStore.getState().getQuestionHistory(selectedExerciseId);

    let completed: number[] = [];
    let in_process: number[] = [];
    if (history && history.exercise_id === selectedExerciseId) {
      completed = history.completed || [];
      in_process = history.in_process || [];
    } else {
      const historyResponse = await exerciseServiceInstance.getExerciseQuestionHistory(selectedExerciseId);
      if (historyResponse?.questions?.length > 0) {
        const normalized = historyResponse.questions[0];
        useQuestionsStore.getState().setQuestionHistory({
          exercise_id: selectedExerciseId,
          current: normalized.current || [],
          completed: normalized.completed || [],
          in_process: normalized.in_process || [],
        });
        completed = normalized.completed || [];
        in_process = normalized.in_process || [];
      }
    }

    setExerciseQuestions(
      questions.map((q: any) => ({
        ...q,
        isCompleted: completed.includes(q.id),
        isInProcess: in_process.includes(q.id)
      }))
    );

    try {
      audioServiceInstance.handleTextToAudioByKey("exercisequestions_voice_exaplin", true);
    } catch (audioError) {
      console.error("Error playing audio:", audioError);
    }
  };

  const handleChosenQuestionSolution = (action: string) => {
    if (!selectedQuestionId) {
      showErrorToast(t("selectQuestion"));
      return;
    }
    const payload = {
      exercise_id: selectedExerciseId,
      selected_question: selectedQuestion,
      selected_question_id: selectedQuestionId
    };
    if (action === "solution_by_steps") {
      handleTracking("click_way_to_solution");
      navigate(ROUTERS_PATH.QUESTIONSTEPS, {
        state: { api: action, payload },
      });
    } else {
      handleTracking("click_immediate_solution");
      navigate(ROUTERS_PATH.CHAT, {
        state: {
          api: action,
          payload,
          from: "exercise-questions",
          selectedQuestionId: selectedQuestionId,
          selectedQuestionLetter: selectedQuestionLetter,
        },
      })
    }
  };
  const handleSelectQuestion = useCallback((question: { id: number, question: string, chart_image_data?: string }, hebrewLetter: string) => {
    if (question.chart_image_data) {
      useQuestionsStore.getState().setChart({
        question_id: question.id,
        chart_image_base64: question.chart_image_data,
      });
    }
    setCurrentSelectedQuestion(question.id);
    setSelectedQuestion(question.question)
    useQuestionsStore.getState().setSelectedQuestionLetter(hebrewLetter);
    setSelectedQuestionId(question.id);
    setSelectedQuestionLetter(hebrewLetter);
  }, [setCurrentSelectedQuestion]);

  const handleBackClick = () => {
    navigate(ROUTERS_PATH.DISPLAYEXERCISE);
  };


  return (
    <div className="exercise-question-page">
      <ChatHeader
        chatHeading={t("sectionsOfTheExercise")}
        onBackClick={handleBackClick}
      />
      <div className="exercise-question-body">
        <ExerciseQuestionList
          exerciseQuestions={exerciseQuestions}
          selectedQuestionId={selectedQuestionId}
          hebrewLetters={hebrewLetters}
          completedQuestions={completedQuestions}
          onSelectQuestion={handleSelectQuestion}
        />
        <div className="exercise-question-button">
          {/* <Button
            text={t("solutionThisSection")}
            className="button-border"
            color="blue"
            onClick={() => handleChosenQuestionSolution("all_steps_solution")} // Default to first question
          /> */}
          <Button
            text={t("wayToSolution")}
            color="blue"
            onClick={() => handleChosenQuestionSolution("solution_by_steps")} // Default to first question
          />
        </div>
      </div>
    </div>
  );
}
